/**
 * ファイルダウンロード用のユーティリティ関数
 */

declare global {
  interface ImportMetaEnv {
    readonly VITE_API_URL?: string;
  }

  interface ImportMeta {
    readonly env: ImportMetaEnv;
  }
}

/**
 * API ベース URL を取得
 */
export const getApiBaseUrl = (): string => {
  if (typeof import.meta !== "undefined" && import.meta.env?.VITE_API_URL) {
    return import.meta.env.VITE_API_URL;
  }
  return "http://localhost:3000";
};

/**
 * Cookie から値を取得
 */
export const getSecureCookie = (name: string): string | null => {
  const cookieMap = document.cookie
    .split(";")
    .map((entry) => entry.trim())
    .filter(Boolean)
    .reduce<Record<string, string>>((acc, entry) => {
      const separatorIndex = entry.indexOf("=");
      if (separatorIndex <= 0) return acc;
      const key = entry.slice(0, separatorIndex);
      const value = entry.slice(separatorIndex + 1);
      acc[key] = value;
      return acc;
    }, {});

  if (cookieMap[name]) {
    return cookieMap[name];
  }

  const hostname = typeof window !== "undefined" ? window.location.hostname : "";
  const isStagingHost = hostname.includes("stg.ippon-cloud.com");
  const stagingName = name.startsWith("stg_") ? name : `stg_${name}`;
  const plainName = name.startsWith("stg_") ? name.slice(4) : name;

  if (isStagingHost && cookieMap[stagingName]) {
    return cookieMap[stagingName];
  }
  if (cookieMap[plainName]) {
    return cookieMap[plainName];
  }

  return null;
};

/**
 * ファイル名からContent-Typeを判定
 */
export const getContentTypeFromFileName = (fileName: string): string => {
  const extension = fileName.split(".").pop()?.toLowerCase() || "";
  const contentTypeMap: Record<string, string> = {
    pdf: "application/pdf",
    jpg: "image/jpeg",
    jpeg: "image/jpeg",
    png: "image/png",
    gif: "image/gif",
    webp: "image/webp",
    txt: "text/plain",
    csv: "text/csv",
    xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    xls: "application/vnd.ms-excel",
    docx: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    doc: "application/msword",
  };
  return contentTypeMap[extension] || "application/octet-stream";
};

const MAX_JSON_RESPONSE_SIZE = 50 * 1024 * 1024; // 50MB に増やす
const ERROR_TEXT_SAMPLE_SIZE = 500;

export const processJsonResponse = (
  uint8Array: Uint8Array,
  options: { throwOnNonBufferJson?: boolean } = {}
): Uint8Array | null => {
  const textDecoder = new TextDecoder();
  const text = textDecoder.decode(uint8Array.slice(0, ERROR_TEXT_SAMPLE_SIZE));
  const trimmedText = text.trim();

  if (trimmedText.startsWith("{") && !trimmedText.startsWith("{%")) {
    if (uint8Array.length > MAX_JSON_RESPONSE_SIZE) {
      return null;
    }

    let json;
    try {
      const jsonText = textDecoder.decode(uint8Array);
      json = JSON.parse(jsonText);
    } catch (error) {
      return null;
    }

    if (json.type === "Buffer" && Array.isArray(json.data)) {
      return new Uint8Array(json.data);
    }

    if (options.throwOnNonBufferJson) {
      const errorMsg =
        json.message || json.error || json.errorMessage || JSON.stringify(json);
      console.error("Server error response:", json);
      throw new Error(errorMsg);
    }

    return null;
  }
  return null;
};

/**
 * Blobを作成し、ダウンロードを実行
 */
export const createBlobAndDownload = (
  uint8Array: Uint8Array,
  contentType: string,
  fileName: string
): void => {
  const blob = new Blob([uint8Array as BlobPart], { type: contentType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

/**
 * 署名付きURLからS3キーを抽出
 */
export const extractS3KeyFromUrl = (url: string): string | null => {
  try {
    const urlObj = new URL(url);
    // Get pathname without leading slash and decode it
    return decodeURIComponent(urlObj.pathname.substring(1));
  } catch {
    console.error("Failed to parse URL:", url);
    return null;
  }
};

/**
 * ファイルをサーバー経由でダウンロード
 * @param s3Url - S3署名付きURL
 * @param fileName - ダウンロード時のファイル名
 */
export const downloadFileViaServer = async (
  s3Url: string,
  fileName: string = "download"
): Promise<void> => {
  try {
    // Base64データの場合は直接ダウンロード
    if (s3Url.startsWith("data:")) {
      const a = document.createElement("a");
      a.href = s3Url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      return;
    }

    // S3キーを抽出
    const s3Key = extractS3KeyFromUrl(s3Url);
    if (!s3Key) {
      throw new Error("Failed to extract S3 key from URL");
    }

    // APIベースURLとトークンを取得
    const apiBaseUrl = getApiBaseUrl();
    const token = getSecureCookie("token");

    // サーバー経由でファイルを取得
    const response = await fetch(`${apiBaseUrl}/api/v1/download/file`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({ key: s3Key }),
    });

    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.statusText}`);
    }

    const contentType =
      response.headers.get("content-type") ||
      getContentTypeFromFileName(fileName);
    const arrayBuffer = await response.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);

    const dataToDownload = processJsonResponse(uint8Array) ?? uint8Array;
    createBlobAndDownload(dataToDownload, contentType, fileName);
  } catch (error) {
    console.error("Error downloading file:", error);
    throw error;
  }
};

/**
 * S3キーを直接指定してダウンロード
 * @param s3Key - S3オブジェクトキー (例: "0423994e.../logo/image.png")
 * @param fileName - ダウンロード時のファイル名
 */
export const downloadFileByKey = async (
  s3Key: string,
  fileName: string = "download"
): Promise<void> => {
  try {
    const apiBaseUrl = getApiBaseUrl();
    const token = getSecureCookie("token");

    const response = await fetch(`${apiBaseUrl}/api/v1/download/file`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token && { Authorization: `Bearer ${token}` }),
      },
      body: JSON.stringify({ key: s3Key }),
    });

    if (!response.ok) {
      throw new Error(`Failed to download file: ${response.statusText}`);
    }

    const contentType =
      response.headers.get("content-type") ||
      getContentTypeFromFileName(fileName);
    const arrayBuffer = await response.arrayBuffer();
    const uint8Array = new Uint8Array(arrayBuffer);

    const dataToDownload = processJsonResponse(uint8Array) ?? uint8Array;
    createBlobAndDownload(dataToDownload, contentType, fileName);
  } catch (error) {
    console.error("Error downloading file:", error);
    throw error;
  }
};
